// Keyword search for IconPicker.vue.  The picker offers the MDI 3.5.95 icons
// in mdi-3.5.95-search.json, whose aliases and category tags were borrowed
// from MDI 7.4.47.  The file leaves out the icons with no confident 7.4.47
// match and the brand logos 7.4.47 deprecates; Icons.lookup() still knows
// them, so existing cogs that use them keep rendering.
//
// Show icons by their 3.5.95 name (`n`) only.  The page loads the 3.5.95
// stylesheet, so a renamed icon's 7.4.47 name, which is among its aliases,
// has no CSS class and draws a blank tile (mdi-checkbox-outline is blank;
// the class is mdi-check-box-outline).  Aliases are for searching only.

import Icons, { IconData } from '@/icons';
import { iconAliases } from '@/icons/aliases';
import { iconMeanings } from '@/icons/meanings';
import searchData from '@/icons/mdi-3.5.95-search.json';
import { iconShelves, IconShelf } from '@/icons/shelves';

interface SearchableIcon {
  iconData: IconData;
  tags: string[];
  nameWords: string[];
  nameAndAliasWords: string[];
  allWords: string[];
}

const words = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/[-\s/]+/)
    .filter(word => word);

// Icon name -> the words of every meaning that lists it.
const meaningWords = iconMeanings.reduce(
  (agg: Record<string, string[]>, meaning) => {
    meaning.icons.forEach(name => {
      agg[name] = (agg[name] || []).concat(meaning.words);
    });
    return agg;
  },
  {}
);

const searchableIcons: SearchableIcon[] = searchData.icons.map(icon => {
  const nameWords = words(icon.n);
  // MDI's aliases, then ours from aliases.ts and meanings.ts.
  const aliases = icon.aliases.concat(
    iconAliases[icon.n] || [],
    meaningWords[icon.n] || []
  );
  const nameAndAliasWords = nameWords.concat(...aliases.map(words));

  return {
    // The same IconData the full grid offered, so a pick saves the same
    // 3.5.95 code point and font family.
    iconData: Icons.lookup('MaterialDesignIconFont', parseInt(icon.c, 16)),
    tags: icon.tags,
    nameWords,
    nameAndAliasWords,
    allWords: nameAndAliasWords.concat(...icon.tags.map(words))
  };
});
searchableIcons.sort((a, b) =>
  a.iconData.name < b.iconData.name
    ? -1
    : a.iconData.name > b.iconData.name
    ? 1
    : 0
);

const onShelf = (icon: SearchableIcon, shelf: IconShelf) =>
  icon.tags.some(tag => shelf.tags.includes(tag)) ||
  (shelf.extraIcons || []).includes(icon.iconData.name);

// Every query word must start one of the icon's words.
const matches = (queryWords: string[], iconWords: string[]) =>
  queryWords.every(query => iconWords.some(word => word.startsWith(query)));

// Name matches first, then alias matches, then category matches, each
// alphabetical.
const rankedMatches = (
  query: string,
  candidates: SearchableIcon[]
): SearchableIcon[] => {
  const queryWords = words(query || '');
  const byName: SearchableIcon[] = [];
  const byAlias: SearchableIcon[] = [];
  const byTag: SearchableIcon[] = [];

  candidates.forEach(icon => {
    if (matches(queryWords, icon.nameWords)) {
      byName.push(icon);
    } else if (matches(queryWords, icon.nameAndAliasWords)) {
      byAlias.push(icon);
    } else if (matches(queryWords, icon.allWords)) {
      byTag.push(icon);
    }
  });

  return byName.concat(byAlias, byTag);
};

const findShelf = (shelfName?: string | null) =>
  iconShelves.find(shelf => shelf.name === shelfName);

/**
 * The offered icons on the shelf named `shelfName` (or all of them) that
 * match `query`, best matches first.
 */
export const searchIcons = (
  query: string,
  shelfName?: string | null
): IconData[] => {
  const shelf = findShelf(shelfName);
  const candidates = shelf
    ? searchableIcons.filter(icon => onShelf(icon, shelf))
    : searchableIcons;

  return rankedMatches(query, candidates).map(icon => icon.iconData);
};

export interface ShelfCount {
  name: string;
  count: number;
}

/**
 * The shelves to offer as chips for `query`, each with how many icons on it
 * match.  With no query, every shelf in order; with one, only the shelves
 * with a match, the most matches first.  The selected shelf always stays, so
 * it can be cleared.
 */
export const shelfCounts = (
  query: string,
  selectedShelfName?: string | null
): ShelfCount[] => {
  const matched = rankedMatches(query, searchableIcons);
  const counts = iconShelves.map(shelf => ({
    name: shelf.name,
    count: matched.filter(icon => onShelf(icon, shelf)).length
  }));

  if (!words(query || '').length) {
    return counts;
  }

  // Array.prototype.sort is stable, so ties keep the shelf order.
  return counts
    .filter(shelf => shelf.count || shelf.name === selectedShelfName)
    .sort((a, b) => b.count - a.count);
};
